import { ethers } from "ethers";
import { MARKET_ABI } from "../../../contracts/contracts";

export const MARKET_ADDRESS = "0x4894B177ac9166A2D89f1F920E7EC15a4A015a14";
export const COLLECTION_ADDRESS = "0xF679A38273547875b1730Ae995a1B0d856868843";

export const SUBGRAPH_URL =
  "https://api.studio.thegraph.com/query/1685536/funooni-key-market/version/latest";

const metadataCache = new Map();

export const resolveIPFS = (uri) => {
  if (!uri) return "";
  if (uri.startsWith("ipfs://")) {
    return uri.replace(
      "ipfs://",
      `${process.env.REACT_APP_PINATA_GATEWAY}/ipfs/`
    );
  }
  return uri;
};

const fetchGraphQL = async (query) => {
  try {
    const response = await fetch(SUBGRAPH_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query }),
    });
    return await response.json();
  } catch (error) {
    console.error("GraphQL Error:", error);
    return { data: null };
  }
};

const fetchWithCache = async (url) => {
  if (metadataCache.has(url)) {
    return metadataCache.get(url);
  }

  try {
    const response = await fetch(url);
    const metadata = await response.json();
    metadataCache.set(url, metadata);
    return metadata;
  } catch (error) {
    return null;
  }
};

export const fetchSpecificListing = async (listingId) => {
  const query = `
    {
      listing(id: "${listingId}") {
        id
        active
        pricePerToken
        quantity
        token {
          id
          tokenId
          metadataUri
          collection {
            id
            name
            description
            contractMetadataUri
          }
        }
      }
    }
  `;

  const { data } = await fetchGraphQL(query);
  const listing = data?.listing;

  if (!listing || !listing.active) return null;

  const tokenMetaUrl = resolveIPFS(listing.token.metadataUri);
  const tokenMeta = await fetchWithCache(tokenMetaUrl);
  const priceInEth = Number(listing.pricePerToken) / 1e18;
  if (!tokenMeta) return null;

  let collectionMeta = null;
  if (listing.token.collection.contractMetadataUri) {
    const colMetaUrl = resolveIPFS(
      listing.token.collection.contractMetadataUri
    );
    collectionMeta = await fetchWithCache(colMetaUrl);
  }

  return {
    id: listing.id,
    pricePerToken: listing.pricePerToken,
    priceEth: `${priceInEth} ETH`,
    priceEthRaw: priceInEth.toString(),
    priceUsd: `$${Math.round(priceInEth * 3167)}`,
    quantity: listing.quantity,
    title: tokenMeta.name || "Untitled",
    content: tokenMeta.description || "",
    image: resolveIPFS(tokenMeta.image),
    year: tokenMeta.year || "Unknown",

    collection: {
      name: listing.token.collection.name,
      description: listing.token.collection.description,
      meta: collectionMeta || {},
    },
  };
};

export const buyNFT = async (listingId, provider) => {
  if (!provider) throw new Error("Wallet not connected");

  const signer = await provider.getSigner();
  const marketContract = new ethers.Contract(
    MARKET_ADDRESS,
    MARKET_ABI,
    signer
  );

  const quantity = 2;

  const listingData = await fetchSpecificListing(listingId);
  if (!listingData) throw new Error("Listing not found or inactive");

  if (listingData.quantity < quantity)
    throw new Error("Not enough quantity available");

  let priceString = listingData.priceEth.replace(" ETH", "").trim();

  let pricePerTokenWei;
  try {
    pricePerTokenWei = ethers.parseEther(priceString);
  } catch (err) {
    throw new Error("Invalid price format for purchase");
  }

  const totalPrice = pricePerTokenWei * quantity;

  const walletAddress = await signer.getAddress();
  const balance = await provider.getBalance(walletAddress);
  if (balance < totalPrice) throw new Error("Not enough ETH to buy");

  const tx = await marketContract.buy(listingId, quantity, {
    value: totalPrice,
  });
  await tx.wait();

  return tx;
};
